import { z } from "zod";
import {
  CLE_PAGE_MAX,
  cleNormale,
  clePage,
  rogner,
  SOUS_TITRE_CAHIER_MAX,
  sujetUtilisable,
  TITRE_CAHIER_MAX,
} from "./coloriage-cles";

/**
 * Contrat du module coloriage de la console (spec du 21/09/2026, ADR 0012 ; creation depuis
 * la console, spec du 03/10/2026, #271).
 *
 * Ce fichier vit sous `src/app/` parce que `src/index.ts` appartient au template :
 * backend et frontend le lisent par leur pont `src/app/contract.ts`. Le paquet Python
 * `coloring` parle le meme JSON. Les enumerations disent exactement ce que disent les
 * contraintes CHECK de `migrations/1001_coloriage.sql` et `1006_coloriage_creer.sql` : un
 * test du backend les compare.
 */

export const jugementSchema = z.enum(["bonne", "defaut", "a_jeter"]);
export type Jugement = z.infer<typeof jugementSchema>;

/**
 * Le format du PDF que monte le PC (`coloring.page.FORMATS`) : `carre` est le 8,5 x 8,5 pouces
 * du calendrier de l'Avent (#362), fabrique par le paquet `coloring` depuis la PR #368.
 */
export const formatCahierSchema = z.enum(["a4", "kdp", "carre"]);
/** Le modele du PC qui rend un cahier : celui de `coloriage_cahiers.modele`. */
export const modeleSchema = z.enum(["klein", "zimage", "workflow"]);
/**
 * Le modele d'une version : ceux du PC, et ceux de Gemini (Nano Banana, ticket 4 de la
 * creation depuis la console, #274). L'identifiant exact de l'API vit dans un reglage.
 */
export const modeleVersionSchema = z.enum([
  ...modeleSchema.options,
  "nano-banana-2",
  "nano-banana-pro",
]);
/** `brouillon` : ne dans la console, son sommaire s'ecrit encore, rien n'est rendu (#271). */
export const etatCahierSchema = z.enum(["brouillon", "ouvert", "clos"]);
export const etatComparaisonSchema = z.enum(["ouverte", "close"]);
/** `ecrire` : Claude ecrit le sommaire, lance par l'agent du PC (ticket 3, #273). */
export const typeDemandeSchema = z.enum(["rendre", "monter", "ecrire"]);
export const etatDemandeSchema = z.enum(["en_attente", "en_cours", "faite", "echouee", "annulee"]);
export const grainesSchema = z.enum(["suivantes", "communes"]);
export const etatComfySchema = z.enum(["ok", "injoignable", "occupe"]);
export const auteurVarianteSchema = z.enum(["nicolas", "claude"]);
/** Ou un cahier est ne : pousse par le PC, ou cree dans la console (#271). */
export const origineCahierSchema = z.enum(["pc", "console"]);
/** Qui ecrit le sommaire d'un cahier ne dans la console. */
export const preparationSchema = z.enum(["nicolas", "claude_pc", "claude_session"]);
/** Les niveaux de `coloring.prompts.NIVEAUX` : 3 a 5 ans, 6 a 8 ans, 9 ans et plus. */
export const niveauSchema = z.enum(["tout-petits", "enfants", "grands"]);
/**
 * Qui rend une version : ComfyUI sur le PC, ou Gemini appele par la console (#274). Le nom
 * porte `Coloriage` : les Histoires ont deja un `fournisseurSchema`, et le pont `contract.ts`
 * reexporte tous les modules d'un bloc.
 */
export const fournisseurColoriageSchema = z.enum(["comfy", "gemini"]);
/** Ce que l'agent dit de `claude` sur le PC (ticket 3, #273). */
export const etatClaudeSchema = z.enum(["ok", "absent"]);

/** Reponse de `POST /api/coloriage/machine/ping` : le jeton du PC est accepte. */
export const pingSchema = z.object({
  module: z.literal("coloriage"),
  ok: z.literal(true),
});
export type Ping = z.infer<typeof pingSchema>;

const identifiant = z.number().int().positive();
const compte = z.number().int().nonnegative();
/** Une date ISO 8601, telle que la base la rend (`toISOString`). */
const horodatage = z.string().min(20).max(40);

// ---------------------------------------------------------------------------------------
// Les controles du PC (`coloring.controles.Controle.en_dict()`), tels quels.

export const graviteSchema = z.enum(["erreur", "avertissement"]);
export const signalementSchema = z.object({
  code: z.string().min(1).max(60),
  gravite: graviteSchema,
  message: z.string().max(2000),
});
export const controleSchema = z.object({
  signalements: z.array(signalementSchema).max(50),
  mesures: z.record(z.string(), z.unknown()),
});
export type Signalement = z.infer<typeof signalementSchema>;
export type Controle = z.infer<typeof controleSchema>;

// ---------------------------------------------------------------------------------------
// Le PC pousse : routes `/api/coloriage/machine/*`, jeton COLORIAGE_TOKEN.

/**
 * La cle d'un cahier : le nom de son dossier sur le PC, passe par `coloring.themes.ardoise`
 * (minuscules, chiffres, tirets). Ni `..`, ni `:`, ni nom reserve de Windows : elle finit
 * aussi dans une adresse (`/machine/cahiers/{cle}/choix`). Meme motif que la migration.
 */
export const cleCahierSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,119}$/);
export const clePageSchema = z.string().min(1).max(160);
export const empreinteSchema = z.string().regex(/^[0-9a-f]{64}$/);

export const pagePousseeSchema = z.object({
  rang: identifiant,
  cle: clePageSchema,
  titre: z.string().min(1).max(200),
  sujet: z.string().min(1).max(2000),
  decor: z.string().max(2000).nullable(),
  niveau: z.string().max(40).nullable(),
  /** Le pre-choix du PC (`coloring.livre.pre_choix`), null tant qu'aucune version n'existe. */
  pre_choix_essai: identifiant.nullable(),
});

/** `POST /api/coloriage/machine/cahiers` : cree ou met a jour un cahier et ses pages. */
export const cahierPousseSchema = z.object({
  cle: cleCahierSchema,
  titre: z.string().min(1).max(200),
  sous_titre: z.string().max(300).nullable(),
  mention: z.string().max(300).nullable(),
  recueil: z.record(z.string(), z.unknown()),
  format: formatCahierSchema,
  modele: modeleSchema,
  graine: z.number().int().nonnegative().nullable(),
  pages: z.array(pagePousseeSchema).min(1).max(200),
  /**
   * D'ou vient le dossier pousse : `console` pour un cahier que l'agent a adopte (son
   * `cahier.json` le note), `pc` sinon, et par defaut pour un agent d'avant #271. La console
   * refuse qu'un dossier du PC remplace un cahier ne dans la console (409 `origine_differente`).
   */
  origine: origineCahierSchema.default("pc"),
});
export type CahierPousse = z.infer<typeof cahierPousseSchema>;

/** Ce que la console a deja d'un cahier : le PC ne renvoie jamais une version recue. */
export const versionRecueSchema = z.object({
  id: identifiant,
  page: clePageSchema,
  essai: identifiant,
  empreinte: empreinteSchema,
  /** Faux si la version est arrivee sans son image (envoi coupe entre les deux) : a renvoyer. */
  image: z.boolean(),
});
export const cahierRecuSchema = z.object({
  id: identifiant,
  versions: z.array(versionRecueSchema),
});
export type CahierRecu = z.infer<typeof cahierRecuSchema>;

/**
 * `POST /api/coloriage/machine/versions` : une version, sans son image. Idempotent sur la
 * page, le numero et l'empreinte ; une autre empreinte pour un numero deja recu est refusee
 * (409 `version_differente`), jamais ecrasee.
 */
export const versionPousseeSchema = z.object({
  cahier: cleCahierSchema,
  page: clePageSchema,
  essai: identifiant.max(10_000),
  graine: z.number().int().nonnegative(),
  modele: modeleSchema,
  prompt: z.string().min(1).max(8000),
  /** Identifiant d'une variante (ticket 4) ; null pour le prompt de reference. */
  variante: identifiant.nullable(),
  /** Identifiant d'une comparaison (ticket 4) ; null pour une version du cahier. */
  comparaison: identifiant.nullable(),
  fichier: z.string().min(1).max(260),
  empreinte: empreinteSchema,
  largeur: identifiant.max(8192),
  hauteur: identifiant.max(8192),
  encre: z.number().nullable(),
  fond: z.number().nullable(),
  controle: controleSchema,
});
export type VersionPoussee = z.infer<typeof versionPousseeSchema>;
export const versionPousseeReponseSchema = z.object({
  id: identifiant,
  image: z.boolean(),
});

/**
 * Le choix de Nicolas pour une page : le numero de la version, et l'empreinte de l'image
 * qu'il a jugee. Le PC ne garde le choix que si son fichier a encore cette empreinte : un
 * fichier change depuis la poussee n'est pas l'image que Nicolas a vue.
 */
export const choixDePageSchema = z.object({
  essai: identifiant,
  empreinte: empreinteSchema,
});
export type ChoixDePage = z.infer<typeof choixDePageSchema>;

/**
 * `POST /api/coloriage/machine/cahiers/{cle}/choix` : le choix de Nicolas, page par page.
 * Null : pas encore de choix, le PC prend alors son pre-choix.
 */
export const choixDuCahierSchema = z.object({
  choix: z.record(clePageSchema, choixDePageSchema.nullable()),
});
export type ChoixDuCahier = z.infer<typeof choixDuCahierSchema>;

// ---------------------------------------------------------------------------------------
// Nicolas lit et juge : routes `/api/coloriage/*`, session, liste blanche, `Origin`.

export const cahierResumeSchema = z.object({
  id: identifiant,
  cle: cleCahierSchema,
  titre: z.string(),
  sous_titre: z.string().nullable(),
  format: formatCahierSchema,
  modele: modeleSchema,
  etat: etatCahierSchema,
  /** `console` : ne dans la console (#271) ; `pc` : pousse par le PC. */
  origine: origineCahierSchema,
  /** Qui ecrit le sommaire ; null pour un cahier du PC. */
  preparation: preparationSchema.nullable(),
  pages: compte,
  /** Versions du cahier, hors comparaisons. */
  versions: compte,
  jugees: compte,
  /** Pages dont Nicolas a choisi la version. */
  choisies: compte,
  /** Poids des images gardees en base, en octets. */
  octets_images: compte,
  /** Pages au sujet vide : un brouillon n'est pret a lancer qu'a 0 (#271). */
  pages_sans_sujet: compte,
  maj_le: horodatage,
});
export type CahierResume = z.infer<typeof cahierResumeSchema>;

// ---------------------------------------------------------------------------------------
// Les demandes et l'agent du PC (ticket 3). Nicolas demande, l'agent prend et rend compte.

/** `POST /api/coloriage/demandes` : rendre N versions des pages cochees, ou monter le PDF. */
export const nouvelleDemandeSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("rendre"),
    cahier: identifiant,
    /** Les identifiants des pages cochees. */
    pages: z.array(identifiant).min(1).max(200),
    versions_par_page: z.number().int().min(1).max(10),
  }),
  z.object({
    type: z.literal("monter"),
    cahier: identifiant,
  }),
]);
export type NouvelleDemande = z.infer<typeof nouvelleDemandeSchema>;

/**
 * Une page d'une demande. `essais` : les numeros de version a rendre, fixes a la creation
 * (au-dela de tout numero deja pris ou reserve par une autre demande) : des graines jamais
 * tirees, et un agent qui reprend sait exactement ce qui reste. Vide pour `monter`.
 */
export const pageDemandeeSchema = z.object({
  id: identifiant,
  rang: identifiant,
  cle: clePageSchema,
  titre: z.string(),
  essais: z.array(identifiant),
  /**
   * Pour une demande de comparaison (ticket 4) : par numero, la variante a employer (null
   * pour la reference) et le rang de graine `k`. Deux variantes au meme `k` recoivent la
   * meme graine, `graine_version(depart, rang, k)` : seule la phrase change. Null pour un
   * rendu ordinaire, ou chaque numero prend la graine de son propre numero.
   */
  plan: z
    .array(z.object({ essai: identifiant, variante: identifiant.nullable(), k: identifiant }))
    .nullable(),
});

/** Ce que rend un montage : le PDF reste sur le PC (4,4 Mo pour 26 pages, trop lourd ici). */
export const resultatMontageSchema = z.object({
  pdf: z.string().min(1).max(500),
  pages: compte,
});

export const demandeSchema = z.object({
  id: identifiant,
  type: typeDemandeSchema,
  cahier: z.object({ id: identifiant, cle: cleCahierSchema, titre: z.string() }),
  pages: z.array(pageDemandeeSchema),
  versions_par_page: identifiant.nullable(),
  /** La comparaison que cette demande rend (ticket 4) ; null pour un rendu du cahier. */
  comparaison: identifiant.nullable(),
  etat: etatDemandeSchema,
  /** Versions rendues et poussees (rendre), ou 0 puis 1 (monter). */
  faites: compte,
  total: compte,
  erreur: z.string().nullable(),
  resultat: resultatMontageSchema.nullable(),
  cree_le: horodatage,
  commencee_le: horodatage.nullable(),
  finie_le: horodatage.nullable(),
});
export type Demande = z.infer<typeof demandeSchema>;

/** Reponse des ecritures sur une demande (creer, annuler, relancer). */
export const demandeReponseSchema = z.object({ demande: demandeSchema });

/** Le dernier signe de vie de l'agent du PC. */
export const agentSchema = z.object({
  vu_le: horodatage,
  /** Vu il y a moins de 90 s : au repos l agent sonde toutes les 5 s, en travail a chaque version. */
  en_ligne: z.boolean(),
  version: z.string().nullable(),
  comfyui: etatComfySchema,
  /** Longueur de la file de ComfyUI, rendus en cours compris ; null si ComfyUI ne repond pas. */
  file: compte.nullable(),
  demande: identifiant.nullable(),
});
export type Agent = z.infer<typeof agentSchema>;

/** `POST /api/coloriage/machine/demandes/prochaine` : le signe de vie de l'agent. */
export const signeDeVieSchema = z.object({
  version: z.string().min(1).max(40),
  comfyui: etatComfySchema,
  file: compte.nullable(),
});
export type SigneDeVie = z.infer<typeof signeDeVieSchema>;
/**
 * La demande de l'agent : celle qu'il avait en cours (il reprend), sinon la plus ancienne en
 * attente si ComfyUI repond et que sa file est vide, sinon null.
 */
export const prochaineDemandeSchema = z.object({ demande: demandeSchema.nullable() });

/** `POST /api/coloriage/machine/demandes/{id}/avancement`. */
export const avancementSchema = z.object({ faites: compte });
/** Reponse de l'avancement et de la fin : l'etat, pour qu'un agent voie une annulation. */
export const etatDeLaDemandeSchema = z.object({ etat: etatDemandeSchema });

/** `POST /api/coloriage/machine/demandes/{id}/fin`. */
export const finDeDemandeSchema = z.discriminatedUnion("etat", [
  z.object({ etat: z.literal("faite"), resultat: resultatMontageSchema.nullable() }),
  z.object({ etat: z.literal("echouee"), erreur: z.string().min(1).max(2000) }),
]);
export type FinDeDemande = z.infer<typeof finDeDemandeSchema>;

/** Pourquoi une page garde le pre-choix du PC a la cloture. */
export const raisonPreChoixSchema = z.enum(["jamais_jugee", "sans_bonne", "sans_choix"]);

/** `GET /api/coloriage/cahiers/{id}/cloture` : ce que clore effacerait, avant de le faire. */
export const clotureApercuSchema = z.object({
  images_effacees: compte,
  octets_liberes: compte,
  /** Les pages sans choix de Nicolas : elles gardent l'image du pre-choix du PC. */
  pages_au_pre_choix: z.array(
    z.object({ rang: identifiant, titre: z.string(), raison: raisonPreChoixSchema }),
  ),
  /** Vrai si une demande est en cours sur le cahier : clore est alors refuse. */
  demande_en_cours: z.boolean(),
});
export type ClotureApercu = z.infer<typeof clotureApercuSchema>;
/** `POST /api/coloriage/cahiers/{id}/clore`. */
export const clotureSchema = z.object({
  images_effacees: compte,
  octets_liberes: compte,
});

/** `GET /api/coloriage` : l'accueil du module. */
export const accueilColoriageSchema = z.object({
  cahiers: z.array(cahierResumeSchema),
  /** Null tant que l'agent ne s'est jamais annonce. */
  agent: agentSchema.nullable(),
  /** Les demandes en attente et en cours, puis les dix dernieres finies des sept derniers jours. */
  demandes: z.array(demandeSchema),
});
export type AccueilColoriage = z.infer<typeof accueilColoriageSchema>;

/**
 * Une version reduite a son point : numero et jugement, et l'empreinte de son image.
 *
 * L'adresse d'une image porte le debut de son empreinte (`/api/coloriage/images/{id}?e=`) :
 * l'image est en cache immuable, et un identifiant reutilise (base restauree d'une
 * sauvegarde) ne doit jamais servir l'image d'une autre version. Le serveur ignore `e`.
 */
export const versionPointSchema = z.object({
  id: identifiant,
  essai: identifiant,
  jugement: jugementSchema.nullable(),
  empreinte: empreinteSchema,
});

export const pageResumeSchema = z.object({
  id: identifiant,
  rang: identifiant,
  cle: clePageSchema,
  titre: z.string(),
  versions: z.array(versionPointSchema),
  choix_version: identifiant.nullable(),
  choix_essai: identifiant.nullable(),
  pre_choix_essai: identifiant.nullable(),
  /** La version a montrer en vignette : la choisie, sinon la premiere bonne, sinon le pre-choix, sinon la premiere. */
  montree: identifiant.nullable(),
  a_juger: compte,
});
export type PageResume = z.infer<typeof pageResumeSchema>;

/** `GET /api/coloriage/cahiers/{id}` : la grille des pages. */
export const cahierDetailSchema = z.object({
  cahier: cahierResumeSchema,
  pages: z.array(pageResumeSchema),
  /** Les demandes en attente et en cours sur ce cahier (ticket 3). */
  demandes: z.array(demandeSchema),
});
export type CahierDetail = z.infer<typeof cahierDetailSchema>;

export const versionDetailSchema = z.object({
  id: identifiant,
  essai: identifiant,
  /** Le sha256 du fichier du PC : l'adresse de l'image en porte le debut (`?e=`). */
  empreinte: empreinteSchema,
  graine: z.number().int().nonnegative(),
  modele: modeleSchema,
  prompt: z.string(),
  /** Nom de la variante (ticket 4) ; null pour le prompt de reference. */
  variante: z.string().nullable(),
  largeur: identifiant,
  hauteur: identifiant,
  encre: z.number().nullable(),
  fond: z.number().nullable(),
  controle: controleSchema,
  jugement: jugementSchema.nullable(),
  note: z.string().nullable(),
  juge_le: horodatage.nullable(),
  /**
   * Vrai si l'image est en base. Sans elle, la page dit qu'elle n'est pas encore arrivee,
   * et la version ne se juge ni ne se choisit : Nicolas ne decide que sur ce qu'il a vu.
   */
  image: z.boolean(),
});
export type VersionDetail = z.infer<typeof versionDetailSchema>;

export const pageVoisineSchema = z.object({
  id: identifiant,
  rang: identifiant,
  titre: z.string(),
});
/**
 * La prochaine version sans jugement du cahier, dans l'ordre des pages puis des numeros. Une
 * version dont l'image n'est pas encore arrivee n'en est jamais une : elle ne se juge pas.
 */
export const prochaineAJugerSchema = z.object({
  page: identifiant,
  rang: identifiant,
  titre: z.string(),
  version: identifiant,
  essai: identifiant,
});
export type ProchaineAJuger = z.infer<typeof prochaineAJugerSchema>;

/** `GET /api/coloriage/pages/{id}` : une page, ses versions et de quoi circuler. */
export const pageDetailSchema = z.object({
  cahier: z.object({
    id: identifiant,
    cle: cleCahierSchema,
    titre: z.string(),
    etat: etatCahierSchema,
  }),
  page: z.object({
    id: identifiant,
    rang: identifiant,
    cle: clePageSchema,
    titre: z.string(),
    sujet: z.string(),
    decor: z.string().nullable(),
    niveau: z.string().nullable(),
    choix_version: identifiant.nullable(),
    choix_essai: identifiant.nullable(),
    pre_choix_essai: identifiant.nullable(),
  }),
  versions: z.array(versionDetailSchema),
  precedente: pageVoisineSchema.nullable(),
  suivante: pageVoisineSchema.nullable(),
  prochaine_a_juger: prochaineAJugerSchema.nullable(),
});
export type PageDetail = z.infer<typeof pageDetailSchema>;

/** `POST /api/coloriage/versions/{id}/jugement` ; un jugement null efface le precedent. */
export const jugerSchema = z.object({
  jugement: jugementSchema.nullable(),
  note: z.string().max(500).nullable(),
});
export type Juger = z.infer<typeof jugerSchema>;
export const jugementReponseSchema = z.object({
  version: versionDetailSchema,
  prochaine_a_juger: prochaineAJugerSchema.nullable(),
});
export type JugementReponse = z.infer<typeof jugementReponseSchema>;

/** `POST /api/coloriage/pages/{id}/choix` ; null retire le choix. */
export const choisirSchema = z.object({
  version: identifiant.nullable(),
});
export type Choisir = z.infer<typeof choisirSchema>;
export const choixReponseSchema = z.object({
  choix_version: identifiant.nullable(),
  choix_essai: identifiant.nullable(),
});
export type ChoixReponse = z.infer<typeof choixReponseSchema>;

// ---------------------------------------------------------------------------------------
// Les variantes du prompt et les comparaisons a l'aveugle (ticket 4, #153).

/**
 * Une phrase d'un sujet remplace : rognee comme `str.strip` de Python (`rogner`), non vide,
 * et, une fois ecrite, avec au moins une lettre ou un chiffre ASCII (`sujetUtilisable`). Le
 * vide garde son propre refus, sans le second.
 */
const texteRemplace = (refus: string) =>
  z
    .string()
    .overwrite(rogner)
    .min(1)
    .max(2000)
    .refine((texte) => texte === "" || sujetUtilisable(texte), refus);

/**
 * Le sujet d'une page, remplace par une variante : les phrases envoyees au modele, pas le
 * titre francais de la page. `decor` null : la variante ne remplace que le sujet.
 *
 * Les deux phrases sont **rognees puis exigees non vides** : un sujet de blancs passerait le
 * schema, figerait la variante des sa premiere comparaison, et le PC refuserait ensuite tout
 * le rendu sur une variante qui ne se modifie plus. Le refus tombe donc ici, a l'entree.
 *
 * **Ecrites, elles ont une lettre ou un chiffre ASCII** (#285), la regle du sommaire d'un
 * brouillon (`sujetUtilisable`, #271). Un sujet `?` passait : `prompt_de_variante`
 * (`coloring/livre.py`) le met a la place de celui de la page, puis `prompt_klein` le refuse
 * (« sujet vide »), et la comparaison echouait sur sa page ; zimage, lui, dessinait `?`. Un
 * decor `?` ne fait rien echouer, mais il remplace celui de la page, puis klein l'ote
 * (`_ecrite`, `coloring/prompts.py`) et zimage l'envoie tel quel : la page perdait son vrai
 * decor, quand l'ecran montrait celui de la variante. Null garde le decor de la page, comme
 * avant.
 */
export const sujetVarianteSchema = z.object({
  sujet: texteRemplace(
    "sujet sans lettre ni chiffre : klein le refuse, la comparaison echouerait sur sa page",
  ),
  decor: texteRemplace(
    "decor sans lettre ni chiffre : le modele n y trouverait rien, null garde celui de la page",
  ).nullable(),
});
export type SujetVariante = z.infer<typeof sujetVarianteSchema>;

/**
 * Le meme sujet a la lecture, sous la regle d'avant #285 : `varianteDe` (backend) vide tous
 * les sujets d'une ligne des qu'un seul ne suit pas ce schema, et le PC rendrait alors la
 * variante avec les sujets du recueil, sans rien dire. Un sujet `?` ecrit avant, ou recopie par
 * une duplication, reste donc lisible : l'ecran le signale sur sa ligne, et une comparaison qui
 * passe par sa page est refusee au lancement (`variante_a_corriger`), avant que l'agent du PC
 * n'arrete toute la demande sur lui.
 */
const sujetVarianteLuSchema = z.object({
  sujet: z.string().trim().min(1).max(2000),
  decor: z.string().trim().min(1).max(2000).nullable(),
});

/**
 * Une phrase d'une variante : l'ouverture, la phrase de style ou celle de rendu. Null : celle
 * de la reference, qui vit dans `coloring.prompts`.
 *
 * Elle se rogne comme `str.strip` de Python (`rogner`). Faite de blancs, elle dit encore
 * « comme la reference » : rognee jusqu'au vide, le backend l'enregistre null, en silence ;
 * vide d'emblee, elle garde son refus d'avant. **Ecrite, elle a une lettre ou un chiffre
 * ASCII** (#293, suite de #285), la regle d'un sujet (`sujetUtilisable`). Une phrase `?`
 * s'enregistrait telle quelle, puis `_ecrite` (`coloring/prompts.py`) lui preferait celle de
 * la reference : l'ecran montrait une phrase que le PC n'employait pas, et une comparaison
 * mesurait la reference contre elle-meme. `_ecrite` ne jette que les blancs et « .,;:!? » ;
 * une phrase `-` ou `_`, il l'envoie telle quelle, et ce n'est pas davantage une phrase en
 * anglais. Ce que cette regle garde, `_ecrite` ne le jette jamais : `tests/test_prompts.py`
 * l'epingle sur la fixture commune. Une phrase deja en base se juge comme le PC la lira, pas
 * par ce schema (`phraseEnregistreeUtilisable`).
 */
export const phraseVarianteSchema = z
  .string()
  .min(1)
  .overwrite(rogner)
  .max(2000)
  .refine(
    (texte) => texte === "" || sujetUtilisable(texte),
    "phrase sans lettre ni chiffre : le modele attend une phrase en anglais, null garde celle de la reference",
  )
  .nullable();

/**
 * Ce que Nicolas ou Claude ecrit d'une variante : `POST /api/coloriage/variantes`,
 * `PUT /api/coloriage/variantes/{id}` et `POST /api/coloriage/machine/variantes` (ou la
 * route pose `auteur` a `claude`, jamais le corps). Un champ null dit « comme la
 * reference » : la console ne garde aucune copie des phrases du paquet, qui derivrait.
 */
export const nouvelleVarianteSchema = z.object({
  nom: z.string().min(1).max(80),
  ouverture: phraseVarianteSchema,
  style: phraseVarianteSchema,
  rendu: phraseVarianteSchema,
  /** Cle de page vers le sujet qui remplace celui du recueil ; 50 pages au plus. */
  sujets: z
    .record(clePageSchema, sujetVarianteSchema)
    .refine((sujets) => Object.keys(sujets).length <= 50, "50 sujets au plus"),
  note: z.string().min(1).max(500).nullable(),
});
export type NouvelleVariante = z.infer<typeof nouvelleVarianteSchema>;

/**
 * Une variante lue. `figee` : une version ou une comparaison l'utilise, elle ne se modifie
 * plus (on la duplique). Les trois compteurs ne portent que sur les comparaisons **closes** :
 * une comparaison en cours donnerait un taux qui bouge a chaque jugement.
 */
export const varianteSchema = z.object({
  id: identifiant,
  nom: z.string(),
  ouverture: z.string().nullable(),
  style: z.string().nullable(),
  rendu: z.string().nullable(),
  sujets: z.record(clePageSchema, sujetVarianteLuSchema),
  auteur: auteurVarianteSchema,
  note: z.string().nullable(),
  archivee: z.boolean(),
  copie_de: identifiant.nullable(),
  figee: z.boolean(),
  comparaisons: compte,
  versions_jugees: compte,
  bonnes: compte,
  cree_le: horodatage,
  maj_le: horodatage,
});
export type Variante = z.infer<typeof varianteSchema>;

/** Les memes compteurs pour la reference, qui n'a pas de ligne en base. */
export const tauxVarianteSchema = z.object({
  comparaisons: compte,
  versions_jugees: compte,
  bonnes: compte,
});

/** `GET /api/coloriage/variantes` et `POST /api/coloriage/machine/variantes/liste`. */
export const listeVariantesSchema = z.object({
  variantes: z.array(varianteSchema),
  reference: tauxVarianteSchema,
});
export type ListeVariantes = z.infer<typeof listeVariantesSchema>;

/** Reponse des ecritures sur une variante (creer, modifier, dupliquer, archiver). */
export const varianteReponseSchema = z.object({ variante: varianteSchema });
export type VarianteReponse = z.infer<typeof varianteReponseSchema>;

/**
 * `POST /api/coloriage/comparaisons` : les memes pages, les memes graines, une variante par
 * colonne. La reference est une variante comme les autres, ecrite `null` ; deux a cinq
 * variantes, sans doublon.
 */
export const nouvelleComparaisonSchema = z.object({
  cahier: identifiant,
  pages: z.array(identifiant).min(1).max(200),
  versions_par_page: z.number().int().min(1).max(10),
  variantes: z
    .array(identifiant.nullable())
    .min(2)
    .max(5)
    .refine(
      (variantes) => new Set(variantes).size === variantes.length,
      "deux fois la meme variante",
    ),
});
export type NouvelleComparaison = z.infer<typeof nouvelleComparaisonSchema>;

/** Une case du tableau : une variante sur une page, ses trois comptes. */
export const caseComparaisonSchema = z.object({
  page: identifiant,
  bonnes: compte,
  defauts: compte,
  a_jeter: compte,
});

/** Une ligne du tableau. `variante` null : la reference, dont l'auteur est le paquet. */
export const ligneComparaisonSchema = z.object({
  variante: z.object({ id: identifiant, nom: z.string() }).nullable(),
  auteur: auteurVarianteSchema.nullable(),
  cases: z.array(caseComparaisonSchema),
  bonnes: compte,
  total: compte,
});
export type LigneComparaison = z.infer<typeof ligneComparaisonSchema>;

/**
 * `GET /api/coloriage/comparaisons/{id}`. `tableau` est **null tant que tout n'est pas
 * juge** : le lire pendant qu'on juge, c'est deja savoir laquelle est laquelle. Les noms des
 * variantes ne sortent qu'avec lui.
 */
export const comparaisonSchema = z.object({
  id: identifiant,
  cahier: z.object({ id: identifiant, titre: z.string() }),
  pages: z.array(z.object({ id: identifiant, rang: identifiant, titre: z.string() })),
  versions_par_page: identifiant,
  variantes_comptees: compte,
  etat: etatComparaisonSchema,
  jugees: compte,
  total: compte,
  /**
   * Les versions arrivees avec leur image. Le jugement a l'aveugle ne s'ouvre qu'a
   * `arrivees === total` : l'agent rend dans l'ordre du plan et pousse au fil de l'eau, donc
   * juger pendant le rendu, c'est juger une serie entiere issue d'une meme variante.
   */
  arrivees: compte,
  demande: identifiant.nullable(),
  cree_le: horodatage,
  close_le: horodatage.nullable(),
  tableau: z.array(ligneComparaisonSchema).nullable(),
});
export type Comparaison = z.infer<typeof comparaisonSchema>;

/** Reponse de la creation : la comparaison et la demande de rendu qu'elle a posee. */
export const comparaisonReponseSchema = z.object({
  comparaison: comparaisonSchema,
  demande: demandeSchema,
});

/**
 * Une version a juger a l'aveugle : ni prompt, ni graine, ni variante, ni numero d'essai.
 * `passage` est le rang de passage dans la comparaison (1 a `total`), pas le numero de la
 * version : il ne dit rien de la page ni de la variante.
 */
export const versionAveugleSchema = z.object({
  version: identifiant,
  rang: identifiant,
  titre: z.string(),
  passage: identifiant,
  image: z.string().min(1),
  largeur: identifiant,
  hauteur: identifiant,
  controle: controleSchema,
});
export type VersionAveugle = z.infer<typeof versionAveugleSchema>;

/** `GET /api/coloriage/comparaisons/{id}/prochaine` et reponse d'un jugement a l'aveugle. */
export const prochaineAveugleSchema = z.object({
  prochaine: versionAveugleSchema.nullable(),
  jugees: compte,
  total: compte,
  /** `arrivees < total` : le rendu n'est pas fini, `prochaine` est null et rien ne se juge. */
  arrivees: compte,
});
export type ProchaineAveugle = z.infer<typeof prochaineAveugleSchema>;

/** `POST /api/coloriage/comparaisons/{id}/jugement` : a l'aveugle, on ne de-juge pas. */
export const jugementAveugleSchema = z.object({
  version: identifiant,
  jugement: jugementSchema,
  note: z.string().max(500).nullable(),
});
export type JugementAveugle = z.infer<typeof jugementAveugleSchema>;

/** `POST /api/coloriage/comparaisons/{id}/clore` : les images partent, les jugements restent. */
export const clotureComparaisonSchema = z.object({
  images_effacees: compte,
  octets_liberes: compte,
});

// ---------------------------------------------------------------------------------------
// Creer depuis la console (spec du 03/10/2026, #271) : un brouillon, son sommaire, son
// lancement ; puis l'agent du PC adopte le cahier a sa premiere demande.

/** Ce que Nicolas dit du cahier a creer : le sommaire s'ecrit ensuite. */
export const briefCahierSchema = z.object({
  theme: z.string().trim().min(1).max(500),
  niveau: niveauSchema,
  dessins: z.number().int().min(1).max(60),
  consigne: z.string().trim().min(1).max(2000).nullable(),
});
export type BriefCahier = z.infer<typeof briefCahierSchema>;

/**
 * `POST /api/coloriage/cahiers` : un brouillon ne dans la console. La cle doit deja etre ce
 * qu'`ardoise` rend (`cleNormale`) : c'est le nom du dossier que l'agent creera, et il refuse
 * un dossier dont le nom ne redonne pas la cle.
 */
export const nouveauCahierSchema = z.object({
  /** 60 caracteres au plus : la page de titre du PC les compose toujours (`TITRE_CAHIER_MAX`). */
  titre: z.string().overwrite(rogner).min(1).max(TITRE_CAHIER_MAX),
  cle: cleCahierSchema.refine(cleNormale, "cle hors de la forme d ardoise, ou nom reserve"),
  format: formatCahierSchema,
  brief: briefCahierSchema,
  preparation: preparationSchema,
});
export type NouveauCahier = z.infer<typeof nouveauCahierSchema>;

/** Reponse de la creation : le cahier, tel que l'accueil le liste. */
export const cahierCreeSchema = z.object({ cahier: cahierResumeSchema });
export type CahierCree = z.infer<typeof cahierCreeSchema>;

/**
 * Une page du sommaire d'un brouillon. Sa cle se calcule (`clePage`), elle ne s'ecrit pas.
 * Un sujet vide est permis tant que le cahier est un brouillon ; le lancement le refuse.
 * Sujet et decor sont rognes : le `trim` de JavaScript ote aussi les espaces insecables.
 */
export const pageSommaireSchema = z.object({
  titre: z.string().overwrite(rogner).min(1).max(200),
  sujet: z.string().overwrite(rogner).max(1000),
  decor: z.string().overwrite(rogner).min(1).max(1000).nullable(),
  niveau: niveauSchema.nullable(),
});
export type PageSommaire = z.infer<typeof pageSommaireSchema>;

/**
 * `PUT /api/coloriage/cahiers/{id}/sommaire` : tout le sommaire, et la version qu'il remplace.
 * Titre et sous-titre tiennent sur la page de titre du PC (60 caracteres) ; la cle de chaque
 * page (`clePage(rang, titre)`) tient dans les 160 caracteres d'une cle de page. Sans ces
 * bornes, le lancement acceptait un sommaire que l'adoption refusait ensuite, et le cahier
 * restait ouvert sans rien a rendre (relecture de #271).
 */
export const sommaireEcritSchema = z
  .object({
    version: z.number().int().nonnegative(),
    titre: z.string().overwrite(rogner).min(1).max(TITRE_CAHIER_MAX),
    sous_titre: z.string().overwrite(rogner).min(1).max(SOUS_TITRE_CAHIER_MAX).nullable(),
    pages: z.array(pageSommaireSchema).min(1).max(60),
  })
  .superRefine((sommaire, contexte) => {
    sommaire.pages.forEach((page, indice) => {
      if (clePage(indice + 1, page.titre).length > CLE_PAGE_MAX) {
        contexte.addIssue({
          code: "custom",
          path: ["pages", indice, "titre"],
          message: `titre trop long pour une cle de page (${CLE_PAGE_MAX} caracteres au plus)`,
        });
      }
    });
  });
export type SommaireEcrit = z.infer<typeof sommaireEcritSchema>;

/** Reponse d'une ecriture du sommaire : la version qui la suivra. */
export const sommaireReponseSchema = z.object({ version: compte });

/** `GET /api/coloriage/cahiers/{id}/sommaire` : le sommaire d'un cahier, brouillon ou lance. */
export const sommaireSchema = z.object({
  cahier: z.object({
    id: identifiant,
    cle: cleCahierSchema,
    titre: z.string(),
    sous_titre: z.string().nullable(),
    format: formatCahierSchema,
    etat: etatCahierSchema,
    origine: origineCahierSchema,
    preparation: preparationSchema.nullable(),
    brief: briefCahierSchema.nullable(),
  }),
  version: compte,
  pages: z.array(
    z.object({
      id: identifiant,
      rang: identifiant,
      cle: clePageSchema,
      titre: z.string(),
      sujet: z.string(),
      decor: z.string().nullable(),
      niveau: niveauSchema.nullable(),
    }),
  ),
  /** Les rangs des pages sans sujet utilisable (`sujetUtilisable`) : le lancement les refuse. */
  sans_sujet: z.array(identifiant),
  /** Les demandes en attente et en cours sur ce cahier. */
  demandes: z.array(demandeSchema),
});
export type Sommaire = z.infer<typeof sommaireSchema>;

/** `POST /api/coloriage/cahiers/{id}/lancer`. Le fournisseur arrive avec Nano Banana (#274). */
export const lancerSchema = z.object({
  versions_par_page: z.number().int().min(1).max(10),
  /**
   * La version du sommaire que Nicolas a sous les yeux : le lancement est refuse (409
   * `sommaire_change`) si un autre ecran, ou Claude, a ecrit depuis. Sans elle, on lancait un
   * sommaire jamais vu (relecture de #271).
   */
  version: z.number().int().nonnegative(),
});
export type Lancer = z.infer<typeof lancerSchema>;

/**
 * `POST /api/coloriage/machine/cahiers/{cle}/sommaire` : ce que l'agent ecrit dans le
 * `cahier.json` d'un cahier ne dans la console, a sa premiere demande. Les cles des pages
 * sont celles de la console, que l'agent reprend telles quelles.
 */
export const sommaireMachineSchema = z.object({
  cle: cleCahierSchema,
  titre: z.string(),
  sous_titre: z.string().nullable(),
  mention: z.string().nullable(),
  format: formatCahierSchema,
  modele: modeleSchema,
  graine: z.number().int().nonnegative(),
  niveau: niveauSchema,
  pages: z
    .array(
      z.object({
        rang: identifiant,
        cle: clePageSchema,
        titre: z.string(),
        sujet: z.string().min(1),
        decor: z.string().nullable(),
        niveau: niveauSchema.nullable(),
      }),
    )
    .min(1),
});
export type SommaireMachine = z.infer<typeof sommaireMachineSchema>;
